import React, { useState, useEffect } from 'react';
import { Input, Label} from './ui';
import { useFormContext } from '../context/FormContext';

const InputMontoObra = () => {//formattedValue, numericValue, setFormattedValue, setNumericValue

	const { formA, updateField } = useFormContext();
	const { datosGenerales } = formA;

	const [formattedValue, setFormattedValue] = useState(datosGenerales.monto_obra || ''); // Valor formateado visualmente (con $)
	const [numericValue, setNumericValue] = useState(null); // Valor numérico real


	// Función para formatear la parte entera agregando puntos cada tres dígitos
	const formatInteger = (numStr) => {
		return numStr
			.split('')
			.reverse()
			.join('')
			.match(/.{1,3}/g)
			?.join('.')
			.split('')
			.reverse()
			.join('') || '0';
	};

	const handleChangeFormattedValue = (newValue) => {
        updateField('formA', 'datosGenerales', 'formattedValue', newValue);
    }

	const handleChangeNumericValue = (newNumber) => {
        updateField('formA', 'datosGenerales', 'numericValue', newNumber);
    }  
	
	const handleChange = (e) => {
		const input = e.target;
		// Usamos el valor formateado ANTES de la edición para calcular la diferencia
		const oldFormattedValue = input.value; 
		const rawValue = oldFormattedValue.replace(/^\$/, ''); // Eliminar el signo $ temporalmente
	
		// 1. Filtrar caracteres válidos
		const cleanValue = rawValue.replace(/[^\d,]/g, '');
	
		// 2. Detectar posición del cursor ANTES de los cambios
		const cursorPosition = input.selectionStart;
	
		// --- CÁLCULO DE PUNTOS Y LONGITUDES ANTES DEL CAMBIO ---
		// Usamos el estado 'formattedValue' (el valor anterior al evento)
		// Nota: Necesitas acceder al 'formattedValue' del scope superior, NO del input.
		// Si la función está definida como se muestra, accede a la prop.
		const oldIntegerPartFormatted = formattedValue.split(',')[0];
		const oldThousandSeparators = oldIntegerPartFormatted.match(/\./g)?.length || 0;
		
		// 3. Procesar partes (entera y decimal)
		const parts = cleanValue.split(',');
		const hasDecimal = parts.length > 1;
	
		// Parte entera
		let integerPart = parts[0].replace(/\D/g, '').replace(/^0+/, '') || '0';
		const formattedInteger = formatInteger(integerPart);
	
		// Parte decimal
		let decimalPart = hasDecimal
			? parts.slice(1).join('').replace(/\D/g, '').substring(0, 2)
			: '';
			
		// 4. Construir nuevo valor formateado
		let newValue = `$${formattedInteger}`;
		
		// Asumimos que la lógica que añade ',00' al inicio es la siguiente
		let addedDecimalPart = '';
		if (formattedInteger !== '0' && !hasDecimal && cleanValue.length > 0) {
			// Esta lógica es la que asumo que genera '$1,00' cuando escribes '1'
			addedDecimalPart = ',00';
		}
	
		if (hasDecimal) {
			newValue += `,${decimalPart}`;
		} else {
			newValue += addedDecimalPart;
		}
	
		// 5. AJUSTE DE POSICIÓN DEL CURSOR (El cambio clave)
		
		// Contar cuántos puntos de miles tiene la NUEVA parte entera
		const newThousandSeparators = formattedInteger.match(/\./g)?.length || 0;
		const separatorDiff = newThousandSeparators - oldThousandSeparators;
	
		let newCursorPosition = cursorPosition + separatorDiff;
	
		// --- AJUSTE CLAVE PARA EVITAR EL SALTO A LOS DECIMALES ---
		
		// Caso 1: Ingreso de primer dígito ('' -> '1' -> '$1,00')
		if (formattedValue === '' && addedDecimalPart === ',00' && integerPart.length === 1) {
			// Si el valor estaba vacío y se añadió ',00', forzamos el cursor después del dígito.
			// La posición debe ser: $ (1) + dígito (1) = 2.
			newCursorPosition = 2;
		} 
		// Caso 2: El cursor se mueve hacia atrás al editar la parte entera
		else {
			// Limitar la posición máxima a la parte entera
			// Esto evita que el cursor pase el último dígito de la parte entera
			const maxPosIntegerPart = `$${formattedInteger}`.length;
			
			// Si el usuario no ha tecleado la coma, el cursor NUNCA debe exceder la parte entera
			if (!hasDecimal && newCursorPosition > maxPosIntegerPart) {
				newCursorPosition = maxPosIntegerPart;
			} 
			// Si el usuario tecleó la coma, la posición debe ser correcta
			else if (hasDecimal) {
				// El cursor debe moverse si se elimina un punto
				// Si el cursor está en medio de los decimales, se mantiene
				
				// Si el cursor estaba en la parte decimal y el cambio no borró toda la parte decimal
				if (cursorPosition > maxPosIntegerPart && decimalPart.length > 0) {
					 // No aplicamos el ajuste de puntos a los decimales
					 // La posición se ajusta automáticamente a menos que se borre un dígito.
					 // Aquí se necesita lógica más compleja para mantener la posición en la parte decimal
					 // La solución simple es no dejar que el cursor salte la parte decimal entera:
					 const currentDecimalLength = newValue.includes(',') ? newValue.split(',')[1].length : 0;
					 const maxPosTotal = maxPosIntegerPart + 1 + currentDecimalLength; // +1 por la coma
					 newCursorPosition = Math.min(newCursorPosition, maxPosTotal);
				}
			}
		}
	
		// 6. Actualizar estados
		setFormattedValue(newValue);
		// Usamos '0' como fallback para el valor decimal
		setNumericValue(parseFloat(`${integerPart}.${decimalPart || '0'}`) || 0);
	
		// 7. Mantener posición del cursor
		// Se usa 'input' que es e.target
		setTimeout(() => {
			input.setSelectionRange(newCursorPosition, newCursorPosition);
		}, 0);
	};

	useEffect(() => {
		if (numericValue) {
			updateField('formA', 'datosGenerales', 'monto_obra', numericValue);
		}
	}, [numericValue]);

	useEffect(() => {
		// Solo actualizar si el valor numérico actual es diferente al valor en datosGenerales
		// Esto evita bucles infinitos y problemas de sincronización
		if (datosGenerales.monto_obra !== numericValue) {
			const numValue = parseFloat(datosGenerales.monto_obra) || 0;
			if (!isNaN(numValue)) {
				setNumericValue(numValue);
				// Formatear el valor numérico correctamente
				const formatted = formatInteger(numValue.toString().split('.')[0]) + 
					(numValue % 1 !== 0 ? `,${numValue.toFixed(2).split('.')[1]}` : ',00');
				setFormattedValue(`$${formatted}`);
			} else {
				setFormattedValue('');
				setNumericValue(0);
			}
		}
	}, [datosGenerales.monto_obra]);
	
	return (
		<div>
			<Label htmlFor="monto_obra">Monto de obra</Label>
			<Input
				type="text"
				value={formattedValue}
				onChange={handleChange}
				placeholder="$ Ej: $1.234,56"
			/>
			<p>Valor formateado: {formattedValue}</p>
			<p>Valor numérico: {numericValue}</p>
		</div>
	);
};


export default InputMontoObra;